import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import ProvinciaSelect from '@/components/geo/ProvinciaSelect.jsx';
import LocalidadSelect from '@/components/geo/LocalidadSelect.jsx';
import {
  createRutaDistribucion,
  deleteRutaDistribucion,
  listRutasDistribucion,
  listSucursalesOperativas,
} from '@/services/sucursalesOperativas.js';

const inputClassName = 'h-8.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';
const panelClassName = 'rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] p-2.5 shadow-[0_10px_30px_rgba(var(--shadow),0.12)] md:p-3';
const emptyForm = { sucursal_origen_id: '', sucursal_destino_id: '', sucursal_via_id: '', provincia_id: '', localidad_destino_id: '', prioridad: '1', activa: true, observaciones: '' };

export default function RutasDistribucionPage() {
  const [sucursales, setSucursales] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [sucursalesData, rutasData] = await Promise.all([
      listSucursalesOperativas({ activa: true }),
      listRutasDistribucion(),
    ]);
    setSucursales(Array.isArray(sucursalesData) ? sucursalesData : []);
    setRows(Array.isArray(rutasData) ? rutasData : []);
  };

  useEffect(() => {
    load().catch((error) => {
      const message = error?.response?.data?.message || 'No se pudieron cargar las rutas';
      if (String(message).toLowerCase().includes('no tiene habilitada la tabla de rutas de distribucion')) {
        Swal.fire('Atención', message, 'warning');
        setRows([]);
        return;
      }
      Swal.fire('Error', message, 'error');
    });
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => [row.sucursal_origen_nombre, row.sucursal_destino_nombre, row.sucursal_via_nombre, row.localidad_destino_nombre].filter(Boolean).some((value) => String(value).toLowerCase().includes(q)));
  }, [rows, search]);

  const handleSave = async () => {
    if (!form.sucursal_destino_id || !form.sucursal_via_id) {
      Swal.fire('Atención', 'Debe indicar sucursal destino y sucursal vía', 'warning');
      return;
    }
    setSaving(true);
    try {
      await createRutaDistribucion({
        sucursal_origen_id: form.sucursal_origen_id ? Number(form.sucursal_origen_id) : null,
        sucursal_destino_id: Number(form.sucursal_destino_id),
        localidad_destino_id: form.localidad_destino_id ? Number(form.localidad_destino_id) : null,
        sucursal_via_id: Number(form.sucursal_via_id),
        prioridad: Number(form.prioridad || 1),
        activa: form.activa,
        observaciones: form.observaciones,
      });
      setForm(emptyForm);
      await load();
      Swal.fire('Listo', 'Regla de ruta guardada', 'success');
    } catch (error) {
      Swal.fire('Error', error?.response?.data?.message || 'No se pudo guardar la regla', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteRutaDistribucion(row.id);
      await load();
    } catch (error) {
      Swal.fire('Error', error?.response?.data?.message || 'No se pudo eliminar la regla', 'error');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-2 px-2 py-2 md:px-3 md:py-3">
      <PageHeader title="Rutas vía / distribución" subtitle="" />

      <section className={`${panelClassName} grid grid-cols-12 gap-2`}>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal origen</label>
          <select className={inputClassName} value={form.sucursal_origen_id} onChange={(e) => setForm((current) => ({ ...current, sucursal_origen_id: e.target.value }))}>
            <option value="">Cualquier origen</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal destino operativa</label>
          <select className={inputClassName} value={form.sucursal_destino_id} onChange={(e) => setForm((current) => ({ ...current, sucursal_destino_id: e.target.value }))}>
            <option value="">Seleccionar destino</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Sucursal vía / hub</label>
          <select className={inputClassName} value={form.sucursal_via_id} onChange={(e) => setForm((current) => ({ ...current, sucursal_via_id: e.target.value }))}>
            <option value="">Seleccionar vía</option>
            {sucursales.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Prioridad</label>
          <input className={inputClassName} value={form.prioridad} onChange={(e) => setForm((current) => ({ ...current, prioridad: e.target.value }))} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <ProvinciaSelect value={form.provincia_id} onChange={(value) => setForm((current) => ({ ...current, provincia_id: String(value || ''), localidad_destino_id: '' }))} label="Provincia destino" />
        </div>
        <div className="col-span-12 md:col-span-3">
          <LocalidadSelect provinciaId={form.provincia_id} value={form.localidad_destino_id} onChange={(value) => setForm((current) => ({ ...current, localidad_destino_id: String(value || '') }))} label="Localidad destino" />
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Observaciones</label>
          <input className={inputClassName} value={form.observaciones} onChange={(e) => setForm((current) => ({ ...current, observaciones: e.target.value }))} />
        </div>
        <div className="col-span-12 md:col-span-2 flex items-center">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activa} onChange={(e) => setForm((current) => ({ ...current, activa: e.target.checked }))} />Activa</label>
        </div>
        <div className="col-span-12">
          <StickyActions onCancel={() => setForm(emptyForm)} onSubmit={handleSave} isSubmitting={saving} primaryLabel="Guardar ruta" secondaryLabel="Limpiar" />
        </div>
      </section>

      <section className={panelClassName}>
        <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Reglas vigentes</h2>
          <input className={`${inputClassName} md:w-[320px]`} placeholder="Buscar origen, destino, vía o localidad" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {filteredRows.length === 0 ? (
          <EmptyState title="Sin reglas de vía" description="Las guías directas seguirán funcionando sin estas reglas." />
        ) : (
          <div className="max-h-[42vh] overflow-auto rounded-2xl border border-[rgba(var(--border),0.55)]">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[rgba(var(--subpanel),0.58)] text-[rgba(var(--muted),0.95)]">
                <tr>
                  <th className="px-3 py-2">Regla</th>
                  <th className="px-3 py-2">Vía</th>
                  <th className="px-3 py-2">Prioridad</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className="border-t border-[rgba(var(--border),0.42)]">
                    <td className="px-3 py-2">{`${row.sucursal_origen_nombre || 'Cualquier origen'} → ${row.localidad_destino_nombre || row.sucursal_destino_nombre}`}</td>
                    <td className="px-3 py-2">{row.sucursal_via_nombre}</td>
                    <td className="px-3 py-2">{row.prioridad}</td>
                    <td className="px-3 py-2 text-right"><button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleDelete(row)}>Eliminar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
